import * as z from "zod";
import type { ApiContext } from "../context";
import {
  AccountNotFound,
  AdminRemovalRefused,
  type ApiNotAuthorized,
  ErrorCode,
  InvalidFormField,
  SlugTaken,
  StoreNotFound,
  type StoreRoleForbidden,
  TaxCollectionRefused,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { SalesReport } from "../model/sales-report";
import {
  type NewStore,
  type ShopCapability,
  ShopPage,
  Store,
  StoreAdmin,
  StoreCapabilities,
  StoreConnection,
  StoreProviderStatus,
  StoreProviders,
  type StoreRole,
  StoreTaxSettings,
  type StoreUpdate,
} from "../model/store";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

const StoresResponse = z.object({ stores: z.array(Store) });
const AdminsResponse = z.object({ admins: z.array(StoreAdmin) });
const CustomerLinkResponse = z.object({ url: z.string() });
const ActionResponse = z.object({ success: z.boolean() });

/**
 * `/stores`. Every `:storeId` route answers a store the caller does not
 * administer with the same `StoreNotFound` as one that does not exist.
 */
export class StoresApi {
  constructor(private readonly t: Transport) {}

  /** Stores the caller is a member of. */
  async list(ctx: ApiContext): Promise<Result<Store[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/stores");
    return forward(
      r,
      (j) => parse(StoresResponse, j).stores,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Creates a store owned by the caller. */
  async create(ctx: ApiContext, store: NewStore): Promise<Result<Store, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/stores", { body: store });
    return forward(
      r,
      (j) => parse(Store, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  async get(ctx: ApiContext, storeId: string): Promise<Result<Store, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}`);
    return forward(
      r,
      (j) => parse(Store, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async update(
    ctx: ApiContext,
    storeId: string,
    update: StoreUpdate,
  ): Promise<Result<Store, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.patch(ctx, `/stores/${storeId}`, { body: update });
    return forward(
      r,
      (j) => parse(Store, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** The accounts that administer a store, each with its role. */
  async admins(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<StoreAdmin[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/admins`);
    return forward(
      r,
      (j) => parse(AdminsResponse, j).admins,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Gives an existing account admin access, at `role` (defaults to Staff on the
   * backend when omitted). A Manager may add Staff only. Adding someone already
   * listed changes nothing about them, role included. Returns the new list.
   */
  async addAdmin(
    ctx: ApiContext,
    storeId: string,
    email: string,
    role?: StoreRole,
  ): Promise<
    Result<StoreAdmin[], ApiNotAuthorized | StoreNotFound | AccountNotFound | StoreRoleForbidden | InvalidFormField>
  > {
    const r = await this.t.post(ctx, `/stores/${storeId}/admins`, { body: role ? { email, role } : { email } });
    return forward(
      r,
      (j) => parse(AdminsResponse, j).admins,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "email" && e.code === ErrorCode.DoesntExist) return new AccountNotFound(email, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * Takes an account's admin access away. Not yourself; a Manager may remove
   * Staff only; the last Owner cannot be removed. Returns the new list.
   */
  async removeAdmin(
    ctx: ApiContext,
    storeId: string,
    userId: string,
  ): Promise<Result<StoreAdmin[], ApiNotAuthorized | StoreNotFound | AdminRemovalRefused | StoreRoleForbidden>> {
    const r = await this.t.delete(ctx, `/stores/${storeId}/admins/${userId}`);
    return forward(
      r,
      (j) => parse(AdminsResponse, j).admins,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "userId") return new AdminRemovalRefused(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Changes an admin's role. Owner only, not on yourself, and demoting the
   * last Owner is refused. Returns the new list.
   */
  async changeAdminRole(
    ctx: ApiContext,
    storeId: string,
    userId: string,
    role: StoreRole,
  ): Promise<
    Result<StoreAdmin[], ApiNotAuthorized | StoreNotFound | AdminRemovalRefused | StoreRoleForbidden | InvalidFormField>
  > {
    const r = await this.t.patch(ctx, `/stores/${storeId}/admins/${userId}`, { body: { role } });
    return forward(
      r,
      (j) => parse(AdminsResponse, j).admins,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "userId") return new AdminRemovalRefused(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Every provider the store has ever connected, with what the merchant can do about each. */
  async providers(ctx: ApiContext, storeId: string): Promise<Result<StoreProviders, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/providers`);
    return forward(
      r,
      (j) => parse(StoreProviders, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** One provider's connection as the provider itself reports it; `connection` is null when never connected. */
  async provider(
    ctx: ApiContext,
    storeId: string,
    providerKey: string,
  ): Promise<Result<StoreProviderStatus, ApiNotAuthorized | StoreNotFound | InvalidFormField>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/providers/${providerKey}`);
    return forward(
      r,
      (j) => parse(StoreProviderStatus, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Re-reads the provider's account and writes the state back. `ValidationError` on `providerKey` for an unknown provider. */
  async refreshProvider(
    ctx: ApiContext,
    storeId: string,
    providerKey: string,
  ): Promise<Result<StoreConnection, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/providers/${providerKey}/refresh`);
    return forward(
      r,
      (j) => parse(StoreConnection, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Checks the provider's configuration against the provider and writes the verdict back. */
  async validateProvider(
    ctx: ApiContext,
    storeId: string,
    providerKey: string,
  ): Promise<Result<StoreConnection, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/providers/${providerKey}/validate`);
    return forward(
      r,
      (j) => parse(StoreConnection, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Drops the provider. Returns the DISCONNECTED row, or null when there was nothing to drop. */
  async disconnectProvider(
    ctx: ApiContext,
    storeId: string,
    providerKey: string,
  ): Promise<Result<StoreConnection | null, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.delete(ctx, `/stores/${storeId}/providers/${providerKey}`);
    return forward(
      r,
      (j) => (j === undefined || j === null || j === "" ? null : parse(StoreConnection, j)),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** The customer-facing entry link for the store: what a QR code or a shared link points at. */
  async customerLink(ctx: ApiContext, storeId: string): Promise<Result<string, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/customer-link`);
    return forward(
      r,
      (j) => parse(CustomerLinkResponse, j).url,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Gives the store a hosted shop page, or moves it to another handle. Omitting
   * `slug` accepts one derived from the name, and keeps an existing one. Clears
   * `websiteUrl`: the two are alternative answers to the same question.
   */
  async claimShopPage(
    ctx: ApiContext,
    storeId: string,
    slug?: string,
  ): Promise<Result<ShopPage, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SlugTaken | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/shop-page`, { body: slug === undefined ? {} : { slug } });
    return forward(
      r,
      (j) => parse(ShopPage, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "slug" && e.code === ErrorCode.AlreadyExist) return new SlugTaken(slug ?? "", e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** What staff have switched on for the shop, and what it is waiting on. */
  async capabilities(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<StoreCapabilities, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/capabilities`);
    return forward(
      r,
      (j) => parse(StoreCapabilities, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Asks HobbyScan to switch `capability` on; the team is emailed and answers
   * at `contactEmail`, which is required when the shop has none on file.
   * Manager or above. Asking twice, or for what the shop has, changes nothing.
   */
  async requestCapability(
    ctx: ApiContext,
    storeId: string,
    capability: ShopCapability,
    contactEmail?: string,
  ): Promise<Result<StoreCapabilities, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/capabilities/requests`, {
      body: contactEmail ? { capability, contactEmail } : { capability },
    });
    return forward(
      r,
      (j) => parse(StoreCapabilities, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "contactEmail") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The sales tax switch next to the Stripe Tax state it depends on. Reads Stripe when an account is connected. */
  async tax(ctx: ApiContext, storeId: string): Promise<Result<StoreTaxSettings, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/tax`);
    return forward(
      r,
      (j) => parse(StoreTaxSettings, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Turning it on needs a branch with a complete address; turning it off is always allowed. */
  async setTax(
    ctx: ApiContext,
    storeId: string,
    collectSalesTax: boolean,
  ): Promise<
    Result<
      StoreTaxSettings,
      ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | TaxCollectionRefused | InvalidFormField
    >
  > {
    const r = await this.t.patch(ctx, `/stores/${storeId}/tax`, { body: { collectSalesTax } });
    return forward(
      r,
      (j) => parse(StoreTaxSettings, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "") return new TaxCollectionRefused(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * Links the account behind `firebaseToken` (a Firebase ID token) as an admin.
   * Unlike `addAdmin` it creates the account when it is new: the colleague is the
   * one signing in.
   */
  async addMember(
    ctx: ApiContext,
    storeId: string,
    firebaseToken: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/members`, { body: { firebaseToken } });
    return forward(
      r,
      (j) => {
        parse(ActionResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * A month of sales, every item sold with its cost and margin. `month` is
   * `YYYY-MM` in the shop's time zone; omitted, it is last month. Manager and up.
   */
  async salesReport(
    ctx: ApiContext,
    storeId: string,
    month?: string,
  ): Promise<Result<SalesReport, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/reports/sales${monthQuery(month)}`);
    return forward(
      r,
      (j) => parse(SalesReport, j),
      (e) => salesReportError(e, storeId),
    );
  }

  /** The same month as a spreadsheet: the CSV text, ready to save as `sales-report.csv`. */
  async salesReportCsv(
    ctx: ApiContext,
    storeId: string,
    month?: string,
  ): Promise<Result<string, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/reports/sales.csv${monthQuery(month)}`);
    return forward(
      r,
      (j) => parse(z.string(), j),
      (e) => salesReportError(e, storeId),
    );
  }
}

const monthQuery = (month?: string) => (month ? `?month=${encodeURIComponent(month)}` : "");

function salesReportError(e: ApiNotAuthorized | ValidationError, storeId: string) {
  if (e instanceof ValidationError) {
    const forbidden = asRoleForbidden(e);
    if (forbidden) return forbidden;
    if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
    return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
  }
  return e;
}
